import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import config from '../config/config';

const StepsHeader = ({ step }) => {
    const steps = [
        { id: 1, label: 'Bienvenue' },
        { id: 2, label: 'Vérification' },
        { id: 3, label: 'Sécurité' },
        { id: 4, label: 'Résumé' },
    ];
    const percent = ((step - 1) / (steps.length - 1)) * 100;

    return (
        <>
            <div className="relative bg-gray-200 rounded-full h-2">
                <div className="left-0 absolute inset-y-0 bg-orange-600 rounded-full transition-all" style={{ width: `${percent}%` }} />
            </div>
            <div className="grid grid-cols-4 mt-3 text-xs sm:text-sm">
                {steps.map((s) => {
                    const active = step === s.id;
                    const done = step > s.id;
                    return (
                        <div key={s.id} className="flex flex-col items-center gap-1">
                            <div
                                className={`w-8 h-8 rounded-full border-2 flex items-center justify-center 
                ${done ? "bg-orange-600 border-orange-600 text-white" : active ? "border-orange-600 text-orange-600" : "border-gray-300 text-gray-500"}`}
                            >
                                {s.id}
                            </div>
                            <div className={`${active ? "text-orange-700 font-medium" : "text-gray-600"}`}>{s.label}</div>
                        </div>
                    );
                })}
            </div>
        </>
    );
};

const RegisterIntervenantPage = () => {
    const navigate = useNavigate();
    const { registerIntervenant, error } = useAuth();
    const { inviteToken = '' } = useParams();

    const [version, setVersion] = useState("");

    useEffect(() => {
        fetch("/version.txt")
            .then(response => response.text())
            .then(text => setVersion(text.trim()));
    }, []);

    const [parsing, setParsing] = useState(true);
    const [parseError, setParseError] = useState(null);

    const [inviteData, setInviteData] = useState({
        email: '',
        ContactNom: '',
        lastName: '',
        IntervenantNom: '',
        declarationUUID: '',
        inviteUUID: '',
        exp: null,
        IsAccountCreated: false,
    });

    // Wizard & form
    const [step, setStep] = useState(1);
    const [code, setCode] = useState('');
    const [sendingCode, setSendingCode] = useState(false);
    const [verifying, setVerifying] = useState(false);

    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // ---- Parse Token côté serveur
    useEffect(() => {
        const run = async () => {
            if (!inviteToken) {
                setParseError("Lien d’invitation invalide : token manquant.");
                setParsing(false);
                return;
            }

            try {
                setParsing(true);
                setParseError(null);

                const url = `${config.apiBaseUrl}/4DACTION/KST_parseInterInviteToken?token=${encodeURIComponent(inviteToken)}`;
                const res = await fetch(url, { method: 'GET' });

                // -- 1) Cas HTTP non OK : on lit le texte, on pose l'erreur, et on STOPPE sans throw
                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    const msg = text?.trim()
                        ? text
                        : `Échec du parsing (HTTP ${res.status})`;
                    setParseError(msg);
                    return; // <-- IMPORTANT : ne pas throw, on stoppe ici
                }

                // -- 2) JSON robuste
                const contentType = res.headers.get('content-type') || '';
                let data = null;
                if (contentType.includes('application/json')) {
                    data = await res.json().catch(() => null);
                } else {
                    // pas du JSON => pose une erreur lisible et stoppe
                    setParseError("Réponse invalide du serveur (format inattendu).");
                    return;
                }
                if (!data) {
                    setParseError("Réponse invalide du serveur.");
                    return;
                }

                // -- 3) Gestion des enveloppes
                let payload = null;
                if (Array.isArray(data) && data.length > 0) {
                    payload = data[0];
                } else if (data?.data) {
                    payload = data.data;
                } else if (data?.payload) {
                    payload = data.payload;
                }

                // -- 4) Signal d'erreur côté API
                if (data.status === 'error' || data.status === 'failed') {
                    setParseError(data.message || "Le lien d’invitation est invalide, expiré ou déjà utilisé.");
                    return; // <-- on STOPPE sans throw
                }

                // -- 5) Validation payload minimale
                if (!payload) {
                    setParseError("Lien d’invitation invalide : données manquantes.");
                    return;
                }

                setInviteData({
                    email: payload.ContactEmail || '',
                    ContactNom: payload.ContactNom || payload.prenom || '',
                    SenderNom: payload.SenderNom || '',
                    SenderSocieteNom: payload.SenderSocieteNom || '',
                    DeclaNom: payload.DeclaNom || '',
                    declarationUUID: payload.declarationUUID || payload.UUID_Declaration || '',
                    inviteUUID: payload.inviteUUID || payload.UUID_Invitation || '',
                    exp: payload.exp || null,
                    IsAccountCreated: data.IsAccountCreated || false,
                    link: payload.linkInviteDeclaration || ''
                });

            } catch (e) {
                // -- 6) Réseau/JS: message lisible et STOP (pas de throw)
                setParseError(
                    e?.message || "Le lien d’invitation est invalide, expiré ou déjà utilisé."
                );
            } finally {
                setParsing(false);
            }
        };

        run();
    }, [inviteToken]);


    // Helpers
    const maskedEmail = useMemo(() => {
        const email = inviteData.email || '';
        if (!/\S+@\S+\.\S+/.test(email)) return email;
        const [u, d] = email.split('@');
        const mUser = u.length <= 2 ? u[0] + '*' : u[0] + '*'.repeat(u.length - 2) + u[u.length - 1];
        return `${mUser}@${d}`;
    }, [inviteData.email]);

    const pwLen = password.length >= 8;
    const pwUpper = /[A-Z]/.test(password);
    const pwDigit = /\d/.test(password);
    const pwSpecial = /[^A-Za-z0-9]/.test(password);
    const pwMatch = password && password === passwordConfirm;
    const passwordOk = pwLen && pwUpper && pwDigit && pwSpecial && pwMatch;

    // Navigation
    const next = async () => {
        if (parsing || parseError) return;

        if (step === 1) {
            setStep(2);
            return;
        }
        if (step === 2) {
            setVerifying(true);
            try {
                // TODO: brancher votre endpoint de vérif serveur
                // const ok = await verifyInviteCode(inviteToken, code.trim());
                const ok = code.trim() === '0000';
                if (!ok) return;
                setStep(3);
            } finally {
                setVerifying(false);
            }
            return;
        }
        if (step === 3) {
            if (!passwordOk) return;
            setStep(4);
            return;
        }
        setStep((s) => Math.min(4, s + 1));
    };

    const prev = () => setStep((s) => Math.max(1, s - 1));

    const resendCode = async () => {
        setSendingCode(true);
        try {
            // TODO: brancher endpoint d’envoi code: await resendInviteCode(inviteToken)
            await new Promise((r) => setTimeout(r, 600));
        } finally {
            setSendingCode(false);
        }
    };

    const onSubmit = async () => {
        if (step !== 4 || !passwordOk || parsing || parseError) return;
        setSubmitting(true);
        try {
            await registerIntervenant({
                inviteToken,
                password,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (

        <div className="flex md:flex-row flex-col w-screen h-screen">
            {/* Left (branding) */}
            <div className="relative flex justify-around items-center bg-gradient-to-tr from-orange-600 to-orange-400 *:border-white w-full md:w-1/2 h-2/5 md:h-full overflow-hidden">
                <div className="z-10 relative px-10">
                    <h1 className="font-sans font-bold text-white text-4xl">SavPlus</h1>
                    <p className="mt-1 text-white">La meilleure gestion de Service Après Vente</p>
                    <a
                        href="https://savplus-btp.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block bg-white mt-4 mb-2 py-2 rounded-2xl w-28 font-bold text-orange-600 text-center transition hover:translate-y-[-2px] duration-300"
                    >
                        En savoir plus
                    </a>
                </div>
                <div className="-bottom-32 -left-40 absolute border-4 border-t-8 border-opacity-30 rounded-full w-80 h-80"></div>
                <div className="-bottom-40 -left-20 absolute border-4 border-t-8 border-opacity-30 rounded-full w-80 h-80"></div>
                <div className="-top-40 -right-0 absolute border-4 border-t-8 border-opacity-30 rounded-full w-80 h-80"></div>
                <div className="-top-20 -right-20 absolute border-4 border-t-8 border-opacity-30 rounded-full w-80 h-80"></div>

                <div className="right-0 bottom-0 absolute">
                    <p className="p-5 px-10 font-bold text-white text-sm" style={{ fontFamily: "'Source Code Pro', monospace" }}>v{version}</p>
                </div>
            </div>

            {/* Right (form) */}
            <div className="flex justify-center items-center bg-white py-10 w-full md:w-1/2 h-3/5 md:h-full">
                {parseError ? (
                    // ---- ÉTATS DE PAGE : loading / erreur de token
                    <div className="flex justify-center items-center bg-gray-50 px-4 w-screen h-screen">
                        <div className="bg-white shadow-sm px-6 py-8 border rounded-2xl w-full max-w-md text-center">
                            <div className="flex justify-center items-center bg-red-100 mx-auto mb-3 rounded-full w-12 h-12 text-red-600">
                                <i className="fa-solid fa-triangle-exclamation" />
                            </div>
                            <h1 className="mb-1 font-bold text-gray-900 text-xl">Invitation non valide</h1>
                            <p className="mb-5 text-gray-600 text-sm">{parseError}</p>
                            <div className="flex justify-center gap-3">
                                <button
                                    onClick={() => navigate('/')}
                                    className="px-4 py-2 border border-gray-300 hover:border-orange-400 rounded-2xl text-gray-700 transition"
                                >
                                    Revenir à l’accueil
                                </button>
                                <Link
                                    to="/login"
                                    className="bg-orange-600 hover:bg-orange-500 px-4 py-2 rounded-2xl text-white transition"
                                >
                                    Se connecter
                                </Link>
                            </div>
                            <p className="mt-4 text-gray-500 text-xs">
                                Si le problème persiste, demandez un nouvel email d’invitation.
                            </p>
                        </div>
                    </div>

                ) : parsing ? (
                    <div className="flex justify-center items-center bg-white w-screen h-screen">
                        <div className="flex items-center gap-3 text-gray-700">
                            <svg className="w-6 h-6 text-orange-600 animate-spin" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                            <span>Validation de l’invitation…</span>
                        </div>
                    </div>
                    // ---- Page principale (token OK)
                ) : inviteData.IsAccountCreated ? (

                    <div className="bg-white w-[20rem] sm:w-[22rem]">
                        <h1 className="mb-1 font-bold text-gray-800 text-2xl text-center">Content de te revoir !</h1>
                        <p className="mb-5 font-normal text-gray-600 text-sm text-center">

                        </p>
                        <div className="pr-4 w-full">
                            <div className="space-y-4">
                                <div className="text-gray-700 text-sm">
                                    <b>{inviteData.SenderNom}</b> de la société <b>{inviteData.SenderSocieteNom}</b> vous a invité à participer à leur déclaration <b>({inviteData.DeclaNom})</b>
                                </div>
                                <div className="text-gray-700 text-sm">
                                    Votre adresse email liée à cette invitation est :
                                </div>
                                <div className="bg-gray-50 px-3 py-2 border rounded-2xl text-gray-800">
                                    {inviteData.email}
                                </div>
                                {(inviteData.ContactNom) && (
                                    <div className="text-gray-500 text-sm">
                                        Invitation pour : <b>{[inviteData.ContactNom].filter(Boolean).join(' ')}</b>
                                    </div>
                                )}
                                {inviteData.IntervenantNom && (
                                    <div className="text-gray-500 text-xs">
                                        (Associé à la société: <code className="font-mono">{inviteData.IntervenantNom}</code>)
                                    </div>
                                )}

                                <h1 className="mb-1 w-full font-semibold text-gray-900 text-lg text-center">Veuillez vous connecter</h1>
                                <p className="mb-5 text-gray-600 text-sm">{parseError}</p>
                                <div className="flex justify-center gap-3">
                                    <button
                                        onClick={() => navigate('/')}
                                        className="px-4 py-2 border border-gray-300 hover:border-orange-400 rounded-2xl text-gray-700 transition"
                                    >
                                        Revenir à l’accueil
                                    </button>
                                    <Link

                                        to="/login"
                                        state={{
                                            prefill: {
                                                email: inviteData.email,
                                                from: "invite",
                                                intervenant: inviteData.IntervenantNom,
                                                declarationUUID: inviteData.declarationUUID,
                                                link: inviteData.link
                                            }
                                        }}

                                        className="bg-orange-600 hover:bg-orange-500 px-4 py-2 rounded-2xl text-white transition"
                                    >
                                        Se connecter
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white w-[20rem] sm:w-[22rem]">
                        <h1 className="mb-1 font-bold text-gray-800 text-2xl text-center">Bienvenue !</h1>
                        <p className="mb-5 font-normal text-gray-600 text-sm text-center">
                            Finalisez votre inscription en 4 étapes
                        </p>

                        <div className="mb-5">
                            <StepsHeader step={step} />
                        </div>

                        {/* Slider */}
                        <div className="relative w-full overflow-hidden">
                            <div
                                className="flex transition-transform duration-500 ease-in-out"
                                style={{ transform: `translateX(-${(step - 1) * 25}%)`, width: '400%' }}
                            >
                                {/* STEP 1 — Bienvenue */}
                                <div className="pr-4 w-full" style={{ width: '25%' }}>
                                    <div className="space-y-4">
                                        <div className="text-gray-700 text-sm">
                                            <b>{inviteData.SenderNom}</b> de la société <b>{inviteData.SenderSocieteNom}</b> vous a invité à participer à leur déclaration <b>({inviteData.DeclaNom})</b>
                                        </div>
                                        <div className="text-gray-700 text-sm">
                                            Votre adresse email liée à cette invitation est :
                                        </div>
                                        <div className="bg-gray-50 px-3 py-2 border rounded-2xl text-gray-800">
                                            {inviteData.email}
                                        </div>
                                        {(inviteData.ContactNom) && (
                                            <div className="text-gray-500 text-sm">
                                                Invitation pour : <b>{[inviteData.ContactNom].filter(Boolean).join(' ')}</b>
                                            </div>
                                        )}
                                        {inviteData.IntervenantNom && (
                                            <div className="text-gray-500 text-xs">
                                                (Associé à la société: <code className="font-mono">{inviteData.IntervenantNom}</code>)
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* STEP 2 — Vérification */}
                                <div className="px-2 w-full" style={{ width: '25%' }}>
                                    <div className="space-y-3">
                                        <div className="text-gray-600 text-sm">
                                            Un code a été envoyé à <b>{maskedEmail || 'votre email'}</b>.
                                            <br />
                                            (Démo : utilisez <b>0000</b>.)
                                        </div>
                                        <input
                                            className="px-3 py-2 border-2 hover:border-orange-400 rounded-2xl outline-none w-full"
                                            value={code}
                                            onChange={(e) => setCode(e.target.value)}
                                            placeholder="Code de confirmation"
                                            inputMode="numeric"
                                        />
                                        <button
                                            type="button"
                                            onClick={resendCode}
                                            disabled={sendingCode}
                                            className="disabled:opacity-50 text-blue-600 text-sm hover:underline"
                                        >
                                            {sendingCode ? 'Envoi…' : 'Renvoyer le code'}
                                        </button>
                                    </div>
                                </div>

                                {/* STEP 3 — Sécurité */}
                                <div className="px-2 w-full" style={{ width: '25%' }}>
                                    <div className="space-y-3">
                                        <input
                                            className="px-3 py-2 border-2 hover:border-orange-400 rounded-2xl outline-none w-full"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Mot de passe"
                                            type="password"
                                        />
                                        <input
                                            className="px-3 py-2 border-2 hover:border-orange-400 rounded-2xl outline-none w-full"
                                            value={passwordConfirm}
                                            onChange={(e) => setPasswordConfirm(e.target.value)}
                                            placeholder="Confirmer le mot de passe"
                                            type="password"
                                        />
                                        <PasswordHints
                                            pass={password}
                                            passConfirm={passwordConfirm}
                                        />
                                    </div>
                                </div>

                                {/* STEP 4 — Résumé */}
                                <div className="pl-4 w-full" style={{ width: '25%' }}>
                                    <div className="space-y-4">
                                        <div className="font-semibold text-gray-800">Récapitulatif</div>
                                        <ul className="space-y-1 text-gray-700 text-sm">
                                            <li><b>Email :</b> {inviteData.email || '—'}</li>
                                            <li><b>Nom/Prénom :</b> {inviteData.ContactNom}</li>
                                            <li><b>Société :</b> {inviteData.IntervenantNom || '—'}</li>
                                            <li><b>Déclaration :</b> {inviteData.declarationUUID || '—'}</li>
                                        </ul>
                                        <div className="bg-orange-50 px-3 py-2 border border-orange-200 rounded-2xl text-orange-700 text-sm">
                                            Votre compte sera <b>enregistré</b> avec ces informations.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Erreurs globales (AuthContext) */}
                            {error && <p className="pt-4 text-red-500 text-sm text-center">{error}</p>}

                            {/* Footer nav */}
                            <div className="flex justify-between items-center mt-6">
                                <button
                                    type="button"
                                    onClick={prev}
                                    disabled={step === 1}
                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl border transition
                  ${step === 1 ? "border-gray-200 text-gray-300 cursor-normal" : "border-gray-300 text-gray-700 hover:border-orange-400"}`}
                                >
                                    <i className="fa-arrow-left fa-solid" /> Précédent
                                </button>

                                {step < 4 ? (
                                    <button
                                        type="button"
                                        onClick={next}
                                        disabled={
                                            parsing ||
                                            (step === 2 && (!code.trim() || verifying)) ||
                                            (step === 3 && !passwordOk)
                                        }
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-white transition
                    ${parsing ||
                                                (step === 2 && (!code.trim() || verifying)) ||
                                                (step === 3 && !passwordOk)
                                                ? "bg-orange-300 cursor-not-allowed"
                                                : "bg-orange-600 hover:bg-orange-500"
                                            }`}
                                    >
                                        {step === 2 && verifying ? 'Vérification…' : 'Suivant'} <i className="fa-arrow-right fa-solid" />
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={onSubmit}
                                        disabled={!passwordOk || submitting}
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-2xl text-white transition
                    ${!passwordOk || submitting ? "bg-orange-300 cursor-not-allowed" : "bg-orange-600 hover:bg-orange-500"}`}
                                    >
                                        {submitting ? "Création..." : "Créer le compte"} <i className="fa-solid fa-check" />
                                    </button>
                                )}
                            </div>

                            {/* Lien login */}
                            <p className="pt-4 text-sm text-center">
                                Déjà un compte ?{' '}
                                <Link to="/login" className="text-blue-500 hover:underline">
                                    Se connecter
                                </Link>
                            </p>
                        </div>
                    </div>
                )}

            </div>

            {/* Mobile BG bottom */}
            <div className="md:hidden visible relative flex justify-around items-center bg-gradient-to-tr from-orange-600 to-orange-400 *:border-white w-full h-1/5 overflow-hidden">
                <div className="-bottom-32 -left-40 absolute border-4 border-t-8 border-opacity-30 rounded-full w-80 h-80"></div>
                <div className="-bottom-40 -left-20 absolute border-4 border-t-8 border-opacity-30 rounded-full w-80 h-80"></div>
                <div className="-top-40 -right-0 absolute border-4 border-t-8 border-opacity-30 rounded-full w-80 h-80"></div>
                <div className="-top-20 -right-20 absolute border-4 border-t-8 border-opacity-30 rounded-full w-80 h-80"></div>
            </div>
        </div >
    );
};

/* Petits helpers UI */
const PasswordHints = ({ pass, passConfirm }) => {
    const pwLen = pass.length >= 8;
    const pwUpper = /[A-Z]/.test(pass);
    const pwDigit = /\d/.test(pass);
    const pwSpecial = /[^A-Za-z0-9]/.test(pass);
    const pwMatch = pass && pass === passConfirm;

    return (
        <div className="space-y-1 text-gray-600 text-xs">
            <div className={pwLen ? 'text-emerald-600' : ''}>• ≥ 8 caractères</div>
            <div className={pwUpper ? 'text-emerald-600' : ''}>• 1 majuscule</div>
            <div className={pwDigit ? 'text-emerald-600' : ''}>• 1 chiffre</div>
            <div className={pwSpecial ? 'text-emerald-600' : ''}>• 1 caractère spécial</div>
            <div className={pwMatch ? 'text-emerald-600' : ''}>• Confirmation identique</div>
        </div>
    );
};

export default RegisterIntervenantPage;
